import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { Control, Controller } from 'react-hook-form'
import { OptionSwitch } from 'src/components'
import {
  RestrictionInputTypeMap,
  RestrictionTypeList,
  RestrictionTypeListId,
  RestrictionTypeListType
} from 'src/api'
import { useTranslation } from 'react-i18next'
import { MassiveModificationInputs } from '../MassiveModificationModal'
import NumberRestrictionInput from './NumberRestrictionInput'
import TimeRestrictionValueTypeRadio from './TimeRestrictionValueTypeRadio'

interface Props {
  containerProps?: BoxProps
  restrictionType?: RestrictionTypeListId
  control: Control<MassiveModificationInputs>
  restrictionTypeList: RestrictionTypeList[]
  fieldIndex?: number
}

const RestrictionInput = ({
  restrictionTypeList,
  containerProps,
  restrictionType,
  control,
  fieldIndex
}: Props) => {
  const { t } = useTranslation()

  if (!restrictionType) return null

  const inputName =
    fieldIndex !== undefined
      ? `restrictions.${fieldIndex}.${restrictionType}`
      : `restrictions.${restrictionType}`

  const restrictionValueTypeInputName =
    fieldIndex !== undefined
      ? `restrictions.${fieldIndex}.timeRestrictionValueType`
      : `restrictions.timeRestrictionValueType`

  const restrictionInputTypes: {
    [key in RestrictionTypeListType]: JSX.Element
  } = {
    boolean: (
      <Controller
        control={control}
        name={inputName as any}
        defaultValue={true}
        render={({ field: { value, ...field } }) => (
          <OptionSwitch
            labels={[t('labels.Closed'), t('labels.Open')]}
            checked={!!value}
            {...field}
          />
        )}
      />
    ),
    number: (
      <Controller
        control={control}
        name={inputName as any}
        rules={{
          required: {
            value: true,
            message: t('validations.required')
          }
        }}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <NumberRestrictionInput
            label={
              restrictionTypeList.find(({ id }) => id === restrictionType)
                ?.name ?? ''
            }
            inputProps={{ inputRef: ref, error: !!error, ...field }}
          />
        )}
      />
    ),
    time: (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Controller
          control={control}
          name={restrictionValueTypeInputName as any}
          defaultValue={'days'}
          render={({ field }) => <TimeRestrictionValueTypeRadio {...field} />}
        />
        <Controller
          control={control}
          name={inputName as any}
          rules={{
            required: {
              value: true,
              message: t('validations.required')
            }
          }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <NumberRestrictionInput
              sx={{ marginLeft: '3rem' }}
              label={
                restrictionTypeList.find(({ id }) => id === restrictionType)
                  ?.name ?? ''
              }
              inputProps={{ inputRef: ref, error: !!error, ...field }}
            />
          )}
        />
      </Box>
    )
  }

  return (
    <Box {...containerProps}>
      {restrictionInputTypes[RestrictionInputTypeMap[restrictionType]]}
    </Box>
  )
}

export default RestrictionInput
